/**
 * The allied-NPC wear ledger — `RosterState.npcWear`, what a keyed NPC carries between
 * encounters (`decisions/0196`).
 *
 * A leaf-handler group of `./leafReducer`'s switch; see `../reducer` for the tier's contract.
 */

import { CommandError, err, ok, type Result } from "@lancer-vtt/shared";
import type { ApplyNpcWearCommand, ClearNpcWearCommand, RosterLeafCommand } from "../commands";
import type { NpcWear, RosterState } from "../types";
import type { RosterApplyResult } from "./context";

const TRACKS = ["hp", "structure", "stress"] as const;

/** `wear` minus its absent fields, or `null` when nothing is left (every track full). */
function sparseWear(wear: NpcWear): NpcWear | null {
  const out: NpcWear = {};
  for (const t of TRACKS) if (wear[t] !== undefined) out[t] = wear[t];
  if (wear.destroyed) out.destroyed = true;
  return Object.keys(out).length === 0 ? null : out;
}

/** `state` with `npcWear[key]` set to `wear`, or removed when `wear` is `null`; the field goes with its last entry. */
function withEntry(state: RosterState, key: string, wear: NpcWear | null): RosterState {
  const { [key]: _removed, ...rest } = state.npcWear ?? {};
  const next = wear === null ? rest : { ...rest, [key]: wear };
  const { npcWear: _old, ...base } = state;
  return Object.keys(next).length === 0 ? base : { ...base, npcWear: next };
}

/** The command that puts `npcWear[key]` back to `prior`. */
function restoreEntry(actorId: string, key: string, prior: NpcWear | undefined): RosterLeafCommand {
  return prior === undefined
    ? { type: "clear_npc_wear", actorId, key }
    : { type: "apply_npc_wear", actorId, key, wear: prior };
}

/**
 * Replace `npcWear[key]` with `cmd.wear`. The tracks must be whole numbers ≥ 0; the key must be
 * non-blank. An empty `wear` removes the entry. Names no pilot, so its event is
 * `roster_ledger_changed`.
 */
export function applyApplyNpcWear(
  state: RosterState,
  cmd: ApplyNpcWearCommand,
): Result<RosterApplyResult, CommandError> {
  if (cmd.key.trim() === "") return err(new CommandError("apply_npc_wear: the key is blank"));
  for (const t of TRACKS) {
    const v = cmd.wear[t];
    if (v !== undefined && !(Number.isInteger(v) && v >= 0)) {
      return err(new CommandError(`apply_npc_wear: ${t} must be a whole number ≥ 0 (got ${v})`));
    }
  }
  const prior = state.npcWear?.[cmd.key];
  return ok({
    state: withEntry(state, cmd.key, sparseWear(cmd.wear)),
    inverse: restoreEntry(cmd.actorId, cmd.key, prior),
    events: [{ type: "roster_ledger_changed", ledger: "npcWear", key: cmd.key }],
  });
}

/** Remove `npcWear[key]`; a no-op when the key carries nothing. */
export function applyClearNpcWear(
  state: RosterState,
  cmd: ClearNpcWearCommand,
): Result<RosterApplyResult, CommandError> {
  const prior = state.npcWear?.[cmd.key];
  return ok({
    state: withEntry(state, cmd.key, null),
    inverse: restoreEntry(cmd.actorId, cmd.key, prior),
    events: [{ type: "roster_ledger_changed", ledger: "npcWear", key: cmd.key }],
  });
}
